import classNames from 'classnames'
import React, { FunctionComponent, PropsWithChildren, ReactNode, useContext, useEffect, useMemo, useRef } from 'react'
import ThemeContext from '@/app/_providers/ThemeProvider/ThemeContext'
import { ButtonContext } from '@/app/_providers/ButtonProvider/ButtonProvider'
import { actionButtonVariations } from '@/app/_components/Button'
import styles from '@/styles/templates/ActionContainer.module.css'
import { useOurStore } from '@/app/_providers/StoreProvider'
import { scrollToTopOfRef } from '@/utils/navigation.utils'

export enum DISPLAY_VARIANT {
  Exhibition = 'exhibition',
  Events = 'events',
}

type Props = {
  /** The title to display */
  title?: string
  /**  content to display in header */
  headerContent?: ReactNode
  /** Footer components */
  footer?: ReactNode
  id?: string
  className?: string
  testId?: string
  /** Variant: Exhibition / Events / etc */
  variant?: DISPLAY_VARIANT
  /** Flag to scroll to top of container on render */
  scrollToTopOnRender?: boolean
  /** Flag to show action button */
  withActionButton?: boolean
}

const ActionContainer: FunctionComponent<PropsWithChildren<Props>> = ({
  title,
  headerContent,
  children,
  footer,
  id,
  className,
  testId,
  variant = DISPLAY_VARIANT.Exhibition,
  withActionButton = false,
  scrollToTopOnRender,
}: PropsWithChildren<Props>) => {
  const { mobile } = useContext(ThemeContext)
  const { buttonState } = useContext(ButtonContext)
  const doActionContainerScroll = useOurStore((store) => store.doActionContainerScroll)
  const updatePartial = useOurStore((store) => store.updatePartial)
  const componentRef = useRef<HTMLDivElement | null>(null)

  const actionButton = useMemo(() => actionButtonVariations(
    buttonState,
    {
      className: styles.ActionButton,
      testId: 'action-button',
    },
  ), [buttonState])

  useEffect(() => {
    if (scrollToTopOnRender && doActionContainerScroll && mobile !== null) {
      scrollToTopOfRef(componentRef, mobile)
    }
    // if the component just re-rendered and has doScroll set to false, reset doScroll back to true after 2 seconds
    let timer: NodeJS.Timeout | undefined
    if (!doActionContainerScroll) {
      timer = setTimeout(() => {
        updatePartial({ doActionContainerScroll: true })
      }, 2000)
    }

    // cleanup the timer when the component unmounts.
    return () => clearTimeout(timer)
  }, [])

  return (
    <div
      ref={componentRef}
      className={classNames(
        styles.Container,
        className,
        { [styles.Events]: variant === DISPLAY_VARIANT.Events },
      )}
      {...(id ? { id } : {})}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {(title || headerContent) && (
        <div
          className={classNames(styles.Header, { [styles.CalendarHeader]: id === 'calendar' })}
          data-testid="action-container-header"
        >
          {title && (
            <h2 className={styles.Title} data-testid={`title-${title}`}>
              {title}
            </h2>
          )}
          {headerContent && (
            <div className={classNames(styles.HeaderContent, { [styles.AboveCalendar]: id === 'calendar' })}>
              {headerContent}
            </div>
          )}
        </div>
      )}
      <div className={styles.Content}>{children}</div>
      {footer && (
        <div className={styles.Footer} data-testid="action-container-footer">
          {footer}
        </div>
      )}
      {withActionButton && !mobile && actionButton}
    </div>
  )
}

export default ActionContainer
